import {
  ActionReducerMapBuilder,
  PayloadAction,
  createSlice,
} from '@reduxjs/toolkit';
import {
  getAdminBlogList,
  updateBlogModeration,
} from '../actions/adminActions';
import { getPageFromUrl } from '../../../utils/paginationUtil';

// * initial state
const initialState: AdminBlogListStateType = {
  blogs: [],
  isLoading: false,
  errorMessage: '',
  paginator: {
    count: 0,
    next: null,
    previous: null,
    currentPage: 1,
  },
};

// * create slice state
const adminBlogSlice = createSlice({
  name: 'adminBlogs',
  initialState,
  reducers: {
    clearAdminBlogList: state => {
      state.blogs = [];
      state.errorMessage = '';
      state.paginator = {
        count: 0,
        next: null,
        previous: null,
        currentPage: 1,
      };
    },
  },
  extraReducers: builder => {
    adminBlogListCases(builder);
    updateBlogModerationCases(builder);
  },
});

// * Named Extra Reducers Functions

const adminBlogListCases = (
  builder: ActionReducerMapBuilder<AdminBlogListStateType>
) => {
  // Get Admin Blog List - Pending
  builder.addCase(getAdminBlogList.pending, state => {
    state.isLoading = true;
    state.errorMessage = '';
  });

  // Get Admin Blog List - Fulfilled
  builder.addCase(getAdminBlogList.fulfilled, (state, action) => {
    state.isLoading = false;
    state.blogs = action.payload.results;

    state.paginator = {
      count: action.payload.count,
      next: getPageFromUrl(action.payload.next),
      previous: getPageFromUrl(action.payload.previous),
      // Store current page from request argument (action.meta.arg)
      currentPage: action.meta.arg.page || 1, 
    };
  });

  // Get Admin Blog List - Rejected
  builder.addCase(getAdminBlogList.rejected, (state, action) => {
    state.isLoading = false;
    state.errorMessage =
      action.payload?.detail || 'Failed to load admin blog list';
  });
};

const updateBlogModerationCases = (
  builder: ActionReducerMapBuilder<AdminBlogListStateType>
) => {
  // Update Blog Moderation - Pending
  builder.addCase(updateBlogModeration.pending, state => {
    state.errorMessage = '';
  });

  // Update Blog Moderation - Fulfilled
  builder.addCase(
    updateBlogModeration.fulfilled,
    (state, action: PayloadAction<BlogCard>) => {
      // Update the blog in the list with the newly moderated data
      const index = state.blogs.findIndex(
        blog => blog.id === action.payload.id
      );
      if (index !== -1) {
        // Assuming action.payload contains the full updated BlogCard object
        state.blogs[index] = action.payload;
      }
    }
  );

  // Update Blog Moderation - Rejected
  builder.addCase(updateBlogModeration.rejected, (state, action) => {
    state.errorMessage =
      action.payload?.detail || 'Failed to update blog moderation status';
  });
};

// * Export actions and reducer
export const { clearAdminBlogList } = adminBlogSlice.actions;
export default adminBlogSlice.reducer;